import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Typography,
} from "@mui/material";
import { supabase } from "../../lib/supabaseClient";

function formatDate(dateString) {
    if (!dateString) return "";

    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    }).format(new Date(dateString));
}

async function getPublishedNews() {
    const { data, error } = await supabase
        .from("news")
        .select("*")
        .eq("published", true)
        .order("published_at", { ascending: false });

    if (error) {
        throw new Error(error.message);
    }

    return data;
}

function NewsSection() {
    const [newsItems, setNewsItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        async function loadNews() {
            try {
                setIsLoading(true);
                setErrorMessage("");

                const data = await getPublishedNews();
                setNewsItems(data);
            } catch (error) {
                console.error(error);
                setErrorMessage("Aktuelles konnte gerade nicht geladen werden.");
            } finally {
                setIsLoading(false);
            }
        }

        loadNews();
    }, []);

    if (!isLoading && !errorMessage && newsItems.length === 0) {
        return null;
    }

    return (
        <Box
            component="section"
            id="aktuelles"
            sx={{
                py: {
                    xs: 8,
                    md: 12,
                },
                backgroundColor: "#f7f3ee",
            }}
        >
            <Container
                maxWidth={false}
                sx={{
                    width: "100%",
                    maxWidth: 1200,
                    mx: "auto",
                    px: {
                        xs: 2,
                        sm: 3,
                        md: 4,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: {
                            xs: "column",
                            md: "row",
                        },
                        alignItems: {
                            xs: "flex-start",
                            md: "flex-end",
                        },
                        justifyContent: "space-between",
                        gap: 3,
                        mb: {
                            xs: 4,
                            md: 6,
                        },
                    }}
                >
                    <Box>
                        <Typography
                            component="p"
                            sx={{
                                mb: 1,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 13,
                                fontWeight: 900,
                                letterSpacing: "0.18em",
                                textTransform: "uppercase",
                                color: "#1d76bd",
                            }}
                        >
                            Aktuelles
                        </Typography>

                        <Typography
                            component="h2"
                            sx={{
                                maxWidth: 620,
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: {
                                    xs: "2.25rem",
                                    sm: "2.8rem",
                                    md: "3.5rem",
                                },
                                lineHeight: 1.05,
                                fontWeight: 700,
                                letterSpacing: "-0.045em",
                                color: "#222",
                            }}
                        >
                            Neues aus dem Hooksieler Lädchen
                        </Typography>
                    </Box>

                    <Typography
                        sx={{
                            maxWidth: 390,
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: {
                                xs: 16,
                                md: 17,
                            },
                            lineHeight: 1.7,
                            color: "rgba(0,0,0,0.62)",
                        }}
                    >
                        Hier finden Sie Neuigkeiten, Angebote und kleine Einblicke direkt
                        aus unserem Geschäft in Hooksiel.
                    </Typography>
                </Box>

                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {errorMessage}
                    </Alert>
                )}

                {isLoading ? (
                    <Box
                        sx={{
                            py: 7,
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "repeat(3, 1fr)",
                            },
                            gap: {
                                xs: 2.5,
                                md: 3,
                            },
                        }}
                    >
                        {newsItems.map((item) => (
                            <Box
                                key={item.id}
                                component="article"
                                sx={{
                                    position: "relative",
                                    backgroundColor: "#fff",
                                    borderRadius: {
                                        xs: 4,
                                        md: 5,
                                    },
                                    overflow: "hidden",
                                    boxShadow:
                                        "0 18px 50px rgba(40, 30, 20, 0.08)",
                                    transition:
                                        "transform 0.25s ease, box-shadow 0.25s ease",

                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: 5,
                                        background:
                                            "linear-gradient(90deg, #1d76bd, #9b7653)",
                                    },

                                    "&:hover": {
                                        transform: {
                                            xs: "none",
                                            md: "translateY(-6px)",
                                        },
                                        boxShadow:
                                            "0 24px 70px rgba(40, 30, 20, 0.13)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        p: {
                                            xs: 2.5,
                                            md: 3,
                                        },
                                        minHeight: {
                                            md: 300,
                                        },
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            alignSelf: "flex-start",
                                            mb: 2,
                                            px: 1.5,
                                            py: 0.7,
                                            borderRadius: 999,
                                            backgroundColor:
                                                "rgba(29,118,189,0.08)",
                                            color: "#1d76bd",
                                            fontFamily:
                                                '"Nunito Sans", sans-serif',
                                            fontSize: 12,
                                            fontWeight: 900,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.08em",
                                        }}
                                    >
                                        Aktuelles
                                    </Box>

                                    <Typography
                                        sx={{
                                            mb: 1.2,
                                            fontFamily:
                                                '"Nunito Sans", sans-serif',
                                            fontSize: 13,
                                            fontWeight: 800,
                                            color: "rgba(0,0,0,0.45)",
                                        }}
                                    >
                                        {formatDate(
                                            item.published_at || item.created_at
                                        )}
                                    </Typography>

                                    <Typography
                                        component="h3"
                                        sx={{
                                            mb: 1.5,
                                            fontFamily:
                                                '"Quicksand", sans-serif',
                                            fontSize: {
                                                xs: "1.55rem",
                                                md: "1.75rem",
                                            },
                                            lineHeight: 1.12,
                                            fontWeight: 700,
                                            color: "#222",
                                        }}
                                    >
                                        {item.title}
                                    </Typography>

                                    {item.excerpt && (
                                        <Typography
                                            sx={{
                                                mb: 2.5,
                                                fontFamily:
                                                    '"Nunito Sans", sans-serif',
                                                fontSize: 16,
                                                lineHeight: 1.65,
                                                color: "rgba(0,0,0,0.62)",
                                            }}
                                        >
                                            {item.excerpt}
                                        </Typography>
                                    )}

                                    <Button
                                        component={RouterLink}
                                        to={`/aktuelles/${item.slug}`}
                                        sx={{
                                            mt: "auto",
                                            alignSelf: "flex-start",
                                            px: 0,
                                            color: "#1d76bd",
                                            fontFamily:
                                                '"Nunito Sans", sans-serif',
                                            fontSize: 14,
                                            fontWeight: 900,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.05em",

                                            "&:hover": {
                                                backgroundColor: "transparent",
                                                color: "#1d76bd",
                                            },
                                        }}
                                    >
                                        Mehr erfahren
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Container>
        </Box>
    );
}

export default NewsSection;